// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { color, layoutContainer, xScreen } from "@flip/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasCategoryContext;

export interface CategoryInformationStyles {
    wrapper?: InjectableCss;
    titleContainer?: InjectableCss;
    title?: TypographyPresentationProps;
    image?: InjectableCss;
    descriptionImageContainer?: InjectableCss;
    descriptionImageWrapper?: InjectableCss;
    description?: InjectableCss;
}

export const categoryInformationStyles: CategoryInformationStyles = {
    titleContainer: {
        css: css`
            ${layoutContainer}
            display: flex;
            flex-wrap: wrap;
            width: 100%;
        `,
    },
    descriptionImageContainer: {
        css: css`
            margin-bottom: 64px;
            background-color: ${color("common.ui16")};
        `,
    },
    descriptionImageWrapper: {
        css: css`
            ${layoutContainer}
            display: flex;
            justify-content: space-between;
            ${xScreen("lte", "md")} {
                flex-direction: column;
                padding-top: 32px;
            }
        `,
    },
    description: {
        css: css`
            width: 50%;
            padding: 32px 16px 32px 0;
            font-size: 16px;
            line-height: 26px;
            pre {
                white-space: pre-line;
            }

            body {
                background-color: transparent;
            }
            ${xScreen("lte", "md")} {
                width: 100%;
                padding: 0;
                margin-bottom: 32px;
                &:empty {
                    display: none;
                }
            }
        `,
    },
    title: {
        variant: "h1",
        css: css`
            margin-bottom: 24px;
            ${xScreen("lte", "md")} {
                margin: 10px 0 30px;
            }
        `,
    },
    image: {
        css: css`
            width: 50%;
            height: 210px;
            display: inline-flex;
            position: relative;
            [data-test-selector="categoryImagePath"] {
                width: 100%;
            }
            ${xScreen("lte", "md")} {
                width: 100%;
                margin-bottom: 32px;
            }
        `,
    },
};

export const CategoryInformation = ({ category }: Props) => {
    const styles = categoryInformationStyles;

    if (!category) {
        return null;
    }

    const hasDescription = !!category.htmlContent;
    const hasImage = !!category.largeImagePath || !!category.smallImagePath;

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.titleContainer}>
                <Typography {...styles.title}>{category.shortDescription}</Typography>
            </StyledWrapper>
            {(hasImage || hasDescription) && (
                <StyledWrapper {...styles.descriptionImageContainer}>
                    <StyledWrapper {...styles.descriptionImageWrapper}>
                        <StyledWrapper {...styles.description}>
                            {createWidgetElement("Category/CategoryDetailBanner", { fields: {} })}
                        </StyledWrapper>
                        {hasImage && (
                            <StyledWrapper {...styles.image}>
                                {createWidgetElement("Category/CategoryImage", { fields: {} })}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withCategory(CategoryInformation),
    definition: {
        group: "Category Details",
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
